import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import {
    initialValues,
    validationSchema,
} from '../../../forms/AccountActivation'
import InputText from '../../../components/InputText/InputText'
import InputPhoneNumber from '../../../components/InputText/components/InputPhoneNumber'
import DropDown from '../../../components/DropDown/DropDown'
import CellLocation from '../../../components/Table/components/CellLocation'
import InputPassword from '../../../components/InputText/components/InputPassword'
import CheckBox from '../../../components/CheckBox/CheckBox'
import Button from '../../../components/Button/Button'
import useScreen from '../../../hooks/useScreen'

const ActivateForm = ({
    onSubmit,
    locations,
    initialValues: initialFormValues,
}) => {
    const {
        handleSubmit,
        isSubmitting,
        setSubmitting,
        setFieldValue,
        errors,
        touched,
        getFieldProps,
        values,
    } = useFormik({
        initialValues,
        validationSchema,
        onSubmit: formValues => {
            setSubmitting(true)
            onSubmit({
                ...formValues,
            })
            setSubmitting(false)
        },
    })
    const { isMobile } = useScreen()

    useEffect(() => {
        if (initialFormValues) {
            Object.keys(initialFormValues)?.forEach(key => {
                setFieldValue(key, initialFormValues[key])
            })
        }
    }, [initialFormValues])

    const formattedShiptos = locations?.map(item => {
        return {
            ...item,
            locationToShow: (
                <CellLocation
                    isInCard={isMobile}
                    label={item?.label}
                    address={item?.address}
                    variant="black"
                />
            ),
        }
    })

    return (
        <form
            className="w-full h-auto flex flex-col items-start justify-start gap-y-2"
            onSubmit={handleSubmit}
        >
            <h2 className="font-bold text-lg text-white mb-4">
                Personal Details
            </h2>
            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-x-6">
                <InputText
                    {...getFieldProps('firstName')}
                    error={touched['firstName'] && errors['firstName']}
                    placeholder=""
                    isRequired
                    label="First Name"
                    variant="black"
                />
                <InputText
                    {...getFieldProps('lastName')}
                    error={touched['lastName'] && errors['lastName']}
                    placeholder=""
                    isRequired
                    label="Last Name"
                    variant="black"
                />
            </div>
            <div className="w-full flex items-center sm:flex-row flex-col justify-between gap-x-6">
                <InputText
                    {...getFieldProps('email')}
                    error={touched['email'] && errors['email']}
                    placeholder=""
                    label="Email Address"
                    variant="black"
                    onChange={null}
                    disabled={true}
                />
                <InputPhoneNumber
                    variant="black"
                    {...getFieldProps('phone')}
                    placeholder="(111) 555-7777"
                    label="Phone Number"
                    isRequired
                    onChange={value => setFieldValue('phone', value)}
                    value={values?.phone}
                    error={touched['phone'] && errors['phone']}
                />
            </div>

            <DropDown
                customRender={({ label, address }) => (
                    <CellLocation
                        isInCard={isMobile}
                        label={label}
                        address={address}
                        variant="black"
                    />
                )}
                data={formattedShiptos}
                keyId="id"
                keyToShow="locationToShow"
                keyToFind="address"
                setSelectedValue={selected =>
                    setFieldValue('primary_location', selected?.value)
                }
                selected={
                    formattedShiptos?.find(
                        s => s?.value === values?.primary_location,
                    ) || null
                }
                required
                placeholder="Select Location"
                label="Primary Office Location"
                variant="black"
                className="w-full"
                error={
                    (touched['primary_location'] &&
                        errors['primary_location']) ||
                    ''
                }
            />

            <div className="flex flex-col items-start justify-start">
                <h2 className="text-white font-bold text-lg">
                    Choose your Password
                </h2>
                <p className="text-black30 font-medium text-sm">
                    Must contain a minimum of 6 characters, one letter, and one
                    number
                </p>
            </div>

            <div className="w-full flex sm:flex-row flex-col items-center justify-between gap-x-6 mt-4">
                <InputPassword
                    {...getFieldProps('password')}
                    error={touched['password'] && errors['password']}
                    label="Password"
                    variant="black"
                    isRequired
                />
                <InputPassword
                    {...getFieldProps('password_confirmation')}
                    label="Password Confirmation"
                    error={
                        touched['password_confirmation'] &&
                        errors['password_confirmation']
                    }
                    variant="black"
                    isRequired
                />
            </div>

            <div className="flex flex-col gap-y-4 items-start justify-start w-full mb-4">
                <CheckBox
                    {...getFieldProps('textNotifications')}
                    defaultChecked={values?.textNotifications}
                    defaultValue={false}
                    correctValue={true}
                    label="I want to receive text notifications"
                    onChange={val => setFieldValue('textNotifications', val)}
                    variant="black"
                />
                <CheckBox
                    {...getFieldProps('emailNotifications')}
                    defaultChecked={values?.emailNotifications}
                    defaultValue={false}
                    correctValue={true}
                    label="I want to receive email notifications"
                    onChange={val => setFieldValue('emailNotifications', val)}
                    variant="black"
                />
            </div>

            <Button
                text="Activate Account"
                type="submit"
                variant="secondary"
                disabled={isSubmitting}
                className="w-full"
            />
        </form>
    )
}

ActivateForm.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    locations: PropTypes.arrayOf(PropTypes.object),
    initialValues: PropTypes.object,
}
export default ActivateForm
